import React, { ChangeEvent } from 'react'
import { FieldHookConfig, useField } from 'formik'
import { Dropdown as UIDropdown } from '@carsys/ui'
import { isInvalid } from './utils'
import { useFormSection } from './FormSection'
import { useRegisterFormField } from './TabbedForm/FormTabPanel'

interface AdditionalOptions {
  clearButton?: boolean
  emptyValue?: any
}

interface DropdownProps<Value>
  extends Omit<React.ComponentProps<typeof UIDropdown>, 'component' | 'defaultValue' | 'name' | 'value'>,
    Pick<FieldHookConfig<Value>, 'name' | 'validate'>,
    AdditionalOptions {}

export function Dropdown<Value>({ name, validate, clearButton, emptyValue = '', options, onChange, ...props }: DropdownProps<Value>) {
  const [input, meta, helpers] = useField<any>({ name, validate })
  useRegisterFormField(name)
  const formSection = useFormSection()

  const changeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    helpers.setValue(event.target.value)
    if (onChange) {
      onChange(event)
    }
  }

  const onClear = () => {
    helpers.setValue(emptyValue)
  }

  // To fix the MUI warning regarding selected option that is out-of-range
  const preparedOptions = options ? (emptyValue ? [...options, { value: emptyValue, text: '', hidden: true }] : options) : undefined

  return (
    <UIDropdown
      disabled={formSection.disabled}
      id={input.name}
      error={isInvalid(meta)}
      {...props}
      inputProps={{
        ...props.inputProps,
        'data-testid': `${name}-input`
      }}
      {...input}
      options={preparedOptions}
      onClear={clearButton ? onClear : undefined}
      value={input.value ?? ''}
      helperText={isInvalid(meta) ? meta.error : props.helperText}
      onChange={changeHandler}
    />
  )
}
